import { Trans } from "@lingui/react/macro";
import { useSession } from "@pontimed/auth-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, type CSSProperties } from "react";

/**
 * /auth/callback — OAuth landing page.
 *
 * apps/api redirects here after successful OAuth callback (Google/Apple/Facebook).
 * Cookies (.pontimed.app) are already set by apps/api. This page just refreshes
 * the session state and routes the user to ?next or the dashboard.
 *
 * Errors land on /auth/error (redirected by apps/api with ?error=<code>).
 */

interface CallbackSearch {
  /** Optional return path after successful sign-in (apps/api forwards from start). */
  next?: string;
  /** apps/api forwards OAuth error codes here (oauth_state_mismatch, oauth_missing_code, etc.) */
  error?: string;
  /** Parked Facebook link token, with `error=oauth_link_requires_email_code`. */
  link?: string;
}

const DEFAULT_RETURN = "/account/user/dashboard";

export const Route = createFileRoute("/auth/callback")({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    next: typeof search.next === "string" ? search.next : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
    link: typeof search.link === "string" ? search.link : undefined,
  }),
  component: CallbackPage,
});

function CallbackPage() {
  const { next, error, link } = Route.useSearch();
  const session = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      // `link` travels on: the error page finishes the Facebook pending link.
      navigate({ to: "/auth/error", search: { error, link }, replace: true });
      return;
    }
    if (session.status === "authenticated") {
      navigate({ to: next ?? DEFAULT_RETURN, replace: true });
    } else if (session.status === "unauthenticated") {
      navigate({ to: "/auth/error", search: { error: "no_session" }, replace: true });
    }
  }, [session.status, next, error, link, navigate]);

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div
        className="border-border bg-card flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border p-6 text-center shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:p-8"
        style={STATUS_CARD_STYLE}
        role="status"
      >
        <span
          aria-hidden="true"
          className="puls-reveal border-primary/20 bg-primary-soft text-primary grid size-14 place-items-center rounded-2xl border"
        >
          <Loader2 className="size-6 animate-spin" />
        </span>
        <h1
          className="puls-reveal font-display text-foreground m-0 text-xl leading-tight font-semibold tracking-[-0.02em]"
          style={{ "--puls-i": 1 } as CSSProperties}
        >
          <Trans>Signing you in…</Trans>
        </h1>
      </div>
    </main>
  );
}

/* ─── Puls status screen ───
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + soft glow behind one
   centred card, state icon in a toned tile, display heading. Presentational
   only. */

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 60% at 50% 40%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const STATUS_CARD_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 45%)",
};

function StatusBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[10%] left-1/2 h-[70%] w-[min(92%,44rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}
